import { isUnclaimedProctor } from "./sessionUtils";

export const centralCreatedAtFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Chicago",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
});

export function formatCreatedAtCentral(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return centralCreatedAtFormatter.format(date);
}

export function isVisibleQueueSession(session) {
  return session?.status === "QUEUE" && isUnclaimedProctor(session?.proctor);
}

export function sortSessionsByCreatedAtAsc(sessions) {
  return [...sessions].sort(
    (left, right) =>
      new Date(left?.createdAt ?? 0).getTime() -
      new Date(right?.createdAt ?? 0).getTime(),
  );
}

export function normalizeQueueSession(session) {
  if (!session) return session;
  return {
    ...session,
    podId: session.podID ?? session.podId,
  };
}

const QUEUE_DISPLAY_FIELDS = new Set([
  "testerName",
  "testerEmail",
  "testerPhone",
  "organizationName",
  "organizationId",
  "createdAt",
  "createdAtCentralDate",
  "type",
  "podID",
  "podId",
]);

function hasPresentValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

export function mergeQueueSessionUpdate(existing, updated) {
  const normalized = normalizeQueueSession(updated);
  if (!normalized?.id) return existing ?? null;
  if (!existing) return normalized;

  const merged = { ...existing };
  Object.entries(normalized).forEach(([key, value]) => {
    if (key === "proctor") {
      if (hasProctorField(updated)) merged.proctor = value;
      return;
    }
    if (QUEUE_DISPLAY_FIELDS.has(key) && !hasPresentValue(value)) return;
    if (value === null || value === undefined) return;
    merged[key] = value;
  });
  if (existing.isUrgent || normalized.isUrgent === true) {
    merged.isUrgent = true;
  }
  return merged;
}

export function isSparseUrgentQueueUpdate(existing, incoming) {
  if (!existing?.id || incoming?.id !== existing.id) return false;
  if (incoming.isUrgent !== true) return false;
  if (
    hasPresentValue(incoming.testerName) ||
    hasPresentValue(incoming.testerEmail)
  ) {
    return false;
  }
  return (
    hasPresentValue(existing.testerName) ||
    hasPresentValue(existing.testerEmail)
  );
}

export function collectQueuePages(pages, { includeClaimed = false } = {}) {
  const byId = new Map();

  for (const page of pages) {
    for (const session of page ?? []) {
      if (!session?.id || session.status !== "QUEUE") continue;
      byId.set(session.id, normalizeQueueSession(session));
    }
  }

  const free = [];
  const claimed = [];
  byId.forEach((session) => {
    if (isUnclaimedProctor(session.proctor)) {
      free.push(session);
    } else if (includeClaimed) {
      claimed.push(session);
    }
  });
  return sortSessionsByCreatedAtAsc(free).concat(
    sortSessionsByCreatedAtAsc(claimed),
  );
}

export function claimedIdsFromSessions(sessions = []) {
  return new Set(
    sessions
      .filter((session) => session?.id && !isUnclaimedProctor(session.proctor))
      .map((session) => session.id),
  );
}

function timestampMs(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function hasProctorField(session) {
  return (
    Boolean(session) && Object.prototype.hasOwnProperty.call(session, "proctor")
  );
}

function isStaleUpdate(existing, incoming) {
  const confirmed = timestampMs(existing?.updatedAt);
  const incomingMs = timestampMs(incoming?.updatedAt);
  if (confirmed == null || incomingMs == null) return false;
  return incomingMs < confirmed;
}

function isConfirmedFreeQueue(session) {
  return (
    hasProctorField(session) &&
    isUnclaimedProctor(session.proctor) &&
    session.status === "QUEUE"
  );
}

function needsClaimRead(existing, incoming, claimed) {
  if (!claimed) return false;
  if (!hasProctorField(incoming)) return true;
  if (!isUnclaimedProctor(incoming.proctor)) return false;
  const confirmed = timestampMs(existing?.updatedAt);
  return confirmed != null && timestampMs(incoming?.updatedAt) == null;
}

function queueResult(
  sessions,
  claimedIds,
  pendingClaims,
  readRequests = [],
  claimVersions = new Map(),
) {
  return {
    sessions,
    claimedIds,
    pendingClaims,
    claimVersions,
    readRequests,
    readIds: readRequests.map((item) => item.id),
  };
}

export function applyQueueEvent(
  sessions,
  claimedIds = new Set(),
  event,
  pendingClaims = new Map(),
  claimVersions = new Map(),
) {
  const nextClaimed = new Set(claimedIds);
  const nextPending = new Map(pendingClaims);
  const nextVersions = new Map(claimVersions);
  if (!event)
    return queueResult(sessions, nextClaimed, nextPending, [], nextVersions);

  if (event.type === "urgent") {
    if (!event.id) {
      return queueResult(sessions, nextClaimed, nextPending, [], nextVersions);
    }
    return queueResult(
      sessions.map((session) =>
        session?.id === event.id ? { ...session, isUrgent: true } : session,
      ),
      nextClaimed,
      nextPending,
      [],
      nextVersions,
    );
  }

  if (event.type === "claim") {
    if (event.id) {
      nextClaimed.add(event.id);
      const generation = (nextVersions.get(event.id) ?? 0) + 1;
      nextVersions.set(event.id, generation);
      nextPending.set(event.id, generation);
      return queueResult(
        sessions,
        nextClaimed,
        nextPending,
        [{ id: event.id, generation }],
        nextVersions,
      );
    }
    return queueResult(sessions, nextClaimed, nextPending, [], nextVersions);
  }

  if (event.type === "create") {
    const session = event.session;
    if (!session?.id) {
      return queueResult(sessions, nextClaimed, nextPending, [], nextVersions);
    }
    if (!isUnclaimedProctor(session.proctor)) {
      nextClaimed.add(session.id);
      return queueResult(sessions, nextClaimed, nextPending, [], nextVersions);
    }
    if (session.status && session.status !== "QUEUE") {
      return queueResult(sessions, nextClaimed, nextPending, [], nextVersions);
    }
    if (sessions.some((item) => item.id === session.id)) {
      return queueResult(sessions, nextClaimed, nextPending, [], nextVersions);
    }
    return queueResult(
      [...sessions, normalizeQueueSession(session)],
      nextClaimed,
      nextPending,
      [],
      nextVersions,
    );
  }

  if (event.type === "update") {
    return applyQueueUpdate(
      sessions,
      nextClaimed,
      nextPending,
      event.session,
      event.claimGeneration,
      nextVersions,
    );
  }

  return queueResult(sessions, nextClaimed, nextPending, [], nextVersions);
}

function confirmationIsObsolete(
  existing,
  updated,
  pendingClaims,
  claimGeneration,
) {
  if (claimGeneration == null || !updated?.id) return false;
  const pending = pendingClaims.get(updated.id);
  if (pending == null || claimGeneration !== pending) return true;
  return Boolean(existing) && isStaleUpdate(existing, updated);
}

function applyQueueUpdate(
  sessions,
  claimedIds,
  pendingClaims,
  updated,
  claimGeneration,
  claimVersions,
) {
  if (!updated?.id) {
    return queueResult(sessions, claimedIds, pendingClaims, [], claimVersions);
  }

  const index = sessions.findIndex((session) => session.id === updated.id);
  const existing = index === -1 ? null : sessions[index];
  if (
    confirmationIsObsolete(existing, updated, pendingClaims, claimGeneration)
  ) {
    return queueResult(sessions, claimedIds, pendingClaims, [], claimVersions);
  }
  if (existing && isStaleUpdate(existing, updated)) {
    return queueResult(sessions, claimedIds, pendingClaims, [], claimVersions);
  }

  const pendingGeneration = pendingClaims.get(updated.id);
  const looksFree =
    !hasProctorField(updated) || isUnclaimedProctor(updated.proctor);
  if (pendingGeneration != null && looksFree && claimGeneration == null) {
    return queueResult(
      sessions,
      claimedIds,
      pendingClaims,
      [{ id: updated.id, generation: pendingGeneration }],
      claimVersions,
    );
  }

  const nextSessions = sessions.slice();
  const merged = {
    ...(existing ?? {}),
    ...normalizeQueueSession(updated),
  };
  if (!hasProctorField(updated) && existing) merged.proctor = existing.proctor;
  if (existing?.isUrgent) merged.isUrgent = true;
  if (index === -1) {
    if (!isConfirmedFreeQueue(updated) && looksFree) {
      return queueResult(
        sessions,
        claimedIds,
        pendingClaims,
        [],
        claimVersions,
      );
    }
    if (!isConfirmedFreeQueue(updated)) {
      if (!looksFree) {
        claimedIds.add(updated.id);
        pendingClaims.delete(updated.id);
      }
      return queueResult(
        sessions,
        claimedIds,
        pendingClaims,
        [],
        claimVersions,
      );
    }
    nextSessions.push(merged);
  } else {
    nextSessions[index] = merged;
  }

  const matchesPending =
    pendingGeneration != null && claimGeneration === pendingGeneration;
  if (
    isConfirmedFreeQueue(updated) &&
    (pendingGeneration == null || matchesPending)
  ) {
    claimedIds.delete(updated.id);
    pendingClaims.delete(updated.id);
  } else if (!looksFree) {
    claimedIds.add(updated.id);
    if (
      pendingGeneration == null ||
      matchesPending ||
      claimGeneration == null
    ) {
      pendingClaims.delete(updated.id);
    }
  }

  return queueResult(
    nextSessions,
    claimedIds,
    pendingClaims,
    [],
    claimVersions,
  );
}

export function reduceQueueEvents(
  sessions,
  claimedIds,
  events = [],
  pendingClaims = new Map(),
  claimVersions = new Map(),
) {
  return events.reduce(
    (state, event) => {
      const next = applyQueueEvent(
        state.sessions,
        state.claimedIds,
        event,
        state.pendingClaims,
        state.claimVersions,
      );
      return {
        sessions: next.sessions,
        claimedIds: next.claimedIds,
        pendingClaims: next.pendingClaims,
        claimVersions: next.claimVersions,
        readRequests: [
          ...(state.readRequests ?? []),
          ...(next.readRequests ?? []),
        ],
        readIds: [
          ...(state.readIds ?? []),
          ...(next.readRequests ?? []).map((item) => item.id),
        ],
      };
    },
    {
      sessions,
      claimedIds,
      pendingClaims,
      claimVersions,
      readRequests: [],
      readIds: [],
    },
  );
}

export function buildInitialQueue(pages, events = [], options) {
  const sessions = collectQueuePages(pages, options);
  return reduceQueueEvents(sessions, claimedIdsFromSessions(sessions), events);
}

export function indexQueuePages(pages) {
  const byId = new Map();
  for (const page of pages) {
    for (const session of page ?? []) {
      if (!session?.id) continue;
      byId.set(session.id, normalizeQueueSession(session));
    }
  }
  return byId;
}

export function missingQueueSessionIds(existingSessions, pages) {
  const incoming = indexQueuePages(pages);
  return existingSessions
    .map((session) => session?.id)
    .filter((id) => id && !incoming.has(id));
}

export async function readMissingQueueSessions(
  existingSessions,
  pages,
  readSession,
) {
  const ids = missingQueueSessionIds(existingSessions, pages);
  const reads = await Promise.all(
    ids.map(async (id) => {
      try {
        return await readSession(id);
      } catch {
        return null;
      }
    }),
  );
  return reads.filter((session) => session?.id);
}

export function reconcileExistingQueue(
  existingSessions,
  claimedIds,
  pages,
  events = [],
  pendingClaims = new Map(),
  claimVersions = new Map(),
) {
  const incoming = indexQueuePages(pages);
  const nextClaimed = new Set(claimedIds);
  const nextPending = new Map(pendingClaims);
  const readRequests = [];
  const nextSessions = existingSessions.map((session) => {
    const found = incoming.get(session.id);
    if (!found) return session;
    if (isStaleUpdate(session, found)) return session;
    const generation = nextPending.get(session.id);
    if (generation != null && isUnclaimedProctor(found.proctor)) {
      readRequests.push({ id: session.id, generation });
      return session;
    }
    if (needsClaimRead(session, found, nextClaimed.has(session.id))) {
      if (generation != null) readRequests.push({ id: session.id, generation });
      return {
        ...session,
        ...found,
        proctor: session.proctor,
        updatedAt: session.updatedAt,
        ...(session.isUrgent ? { isUrgent: true } : {}),
      };
    }
    if (
      isConfirmedFreeQueue(found) &&
      generation == null &&
      nextClaimed.has(session.id)
    ) {
      const keepClaim =
        nextPending.has(session.id) || needsClaimRead(session, found, true);
      if (!keepClaim) {
        nextClaimed.delete(session.id);
      }
    } else if (hasProctorField(found) && !isUnclaimedProctor(found.proctor)) {
      nextClaimed.add(session.id);
      nextPending.delete(session.id);
    }
    return {
      ...session,
      ...found,
      ...(session.isUrgent ? { isUrgent: true } : {}),
    };
  });

  const known = new Set(nextSessions.map((session) => session.id));
  const appended = [];
  for (const session of incoming.values()) {
    if (known.has(session.id) || !isVisibleQueueSession(session)) continue;
    appended.push(session);
    known.add(session.id);
  }

  const reduced = reduceQueueEvents(
    nextSessions.concat(appended),
    nextClaimed,
    events,
    nextPending,
    claimVersions,
  );
  const mergedReads = [...readRequests, ...(reduced.readRequests ?? [])];
  return {
    ...reduced,
    readRequests: mergedReads,
    readIds: mergedReads.map((item) => item.id),
  };
}

export function userPoolGraphqlOperation(operation) {
  return {
    ...operation,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  };
}

export function bindGraphqlSubscription({
  graphql,
  operation,
  onNext,
  onRestart,
  restartLimit = 1,
}) {
  let stopped = false;
  let restarts = 0;
  let current = null;

  function subscribe() {
    current?.unsubscribe();
    current = graphql(userPoolGraphqlOperation(operation)).subscribe({
      next: (value) => {
        if (!stopped) onNext(value);
      },
      error: (error) => {
        if (stopped || restarts >= restartLimit) return;
        restarts += 1;
        subscribe();
        onRestart?.(error);
      },
    });
  }

  subscribe();

  return {
    unsubscribe() {
      stopped = true;
      current?.unsubscribe();
      current = null;
    },
  };
}

export function removeQueueSession(sessions, sessionId) {
  if (!sessionId) return sessions;
  return sessions.filter((session) => session.id !== sessionId);
}

export function reconcileQueueUpdate(
  sessions,
  updated,
  claimedIds = new Set(),
) {
  return applyQueueEvent(sessions, claimedIds, {
    type: "update",
    session: updated,
  });
}

export function isQueueRowClaimed(claimedIds, sessionId) {
  return Boolean(sessionId) && claimedIds?.has(sessionId);
}

export const queueClaimedRowBackground = {
  color: "status-ok",
  opacity: "medium",
};

export const QUEUE_OUTLINE_POD_ID = "4";
export const queuePodOutlineRowClassName = "queue-row-pod-4";

export const queueDefaultRowBorder = {
  side: "bottom",
  size: "xsmall",
  color: "border",
};

export function isOutlinedPodQueueRow(session) {
  const podId = String(session?.podID ?? session?.podId ?? "").trim();
  return podId === QUEUE_OUTLINE_POD_ID;
}

export function queueRowClassName(session) {
  const names = [];
  if (isOutlinedPodQueueRow(session)) {
    names.push(queuePodOutlineRowClassName);
  }
  return names.join(" ") || undefined;
}

export function queueRowDisplayProps(_session, extras = {}) {
  const props = {
    border: queueDefaultRowBorder,
  };
  if (extras.background) props.background = extras.background;
  return props;
}

export function claimedIdsSignature(claimedIds) {
  if (!claimedIds?.size) return "";
  return [...claimedIds].sort().join("\0");
}

export function queueRowProps(sessions, claimedIds) {
  const props = {};
  const byId = new Map();
  (sessions ?? []).forEach((session) => {
    if (session?.id) byId.set(session.id, session);
  });
  const ids = new Set(byId.keys());
  claimedIds?.forEach((id) => ids.add(id));

  ids.forEach((id) => {
    props[id] = queueRowDisplayProps(byId.get(id), {
      background: isQueueRowClaimed(claimedIds, id)
        ? queueClaimedRowBackground
        : undefined,
    });
  });
  return props;
}

export function testerJoinedChecked(value) {
  return value === true;
}

export function testerJoinedUpdateInput(id, next) {
  return { id, hasTesterJoined: next };
}

export async function saveTesterJoined({ previous, next, save }) {
  try {
    const updated = await save(next);
    return {
      value: updated?.hasTesterJoined ?? next,
      error: null,
      historyError: updated?.historyError ?? null,
    };
  } catch (error) {
    return { value: previous, error };
  }
}
